import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import sbeeLogo from "../../assets/sbeelogo.png";
import { MASTER_API, PURCHASE_API } from "../../apiConfig";

const API_BASE = `${PURCHASE_API}/goods-return`;

const styles = {

    wrapper: {
        background: "#e9e9e9",
        minHeight: "100vh",
        padding: "24px",
        boxSizing: "border-box"
    },

    toolbar: {
        maxWidth: "850px",
        margin: "0 auto 12px auto",
        display: "flex",
        justifyContent: "flex-end"
    },

    printBtn: {
        background: "#1d4ed8",
        color: "#fff",
        border: "none",
        borderRadius: "4px",
        padding: "8px 18px",
        fontSize: "14px",
        cursor: "pointer"
    },

    status: {
        maxWidth: "850px",
        margin: "0 auto",
        textAlign: "center",
        padding: "24px",
        fontSize: "14px",
        color: "#444"
    },

    error: {
        color: "#b91c1c"
    },

    slip: {
        background: "#fff",
        maxWidth: "850px",
        margin: "0 auto",
        padding: "28px 32px",
        border: "1px solid #000",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#000",
        boxSizing: "border-box"
    },

    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        borderBottom: "1px solid #000",
        paddingBottom: "10px"
    },

    logo: {
        height: "48px",
        width: "auto",
        objectFit: "contain"
    },

    titleBlock: {
        textAlign: "center"
    },

    title: {
        fontSize: "18px",
        fontWeight: "bold",
        letterSpacing: "0.5px",
        fontFamily: "'Good Times', 'Segoe UI', Calibri, Arial, sans-serif"
    },

    subtitle: {
        fontSize: "13px",
        fontWeight: "bold",
        marginTop: "4px"
    },

    address: {
        fontSize: "11px",
        fontWeight: "normal",
        marginTop: "4px",
        color: "#333"
    },

    table: {
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed"
    },

    metaTd: {
        border: "1px solid #000",
        borderTop: "none",
        padding: "6px 8px",
        fontSize: "13px",
        verticalAlign: "middle"
    },

    metaTdFirstRow: {
        border: "1px solid #000",
        padding: "6px 8px",
        fontSize: "13px",
        verticalAlign: "middle"
    },

    metaLabel: {
        fontWeight: "bold",
        width: "18%"
    },

    metaNote: {
        fontWeight: "normal",
        fontSize: "11px",
        color: "#555"
    },

    instruction: {
        fontWeight: "bold",
        fontSize: "13px",
        padding: "8px 4px",
        borderLeft: "1px solid #000",
        borderRight: "1px solid #000"
    },

    th: {
        border: "1px solid #000",
        padding: "6px 6px",
        fontSize: "12px",
        textAlign: "center",
        fontWeight: "bold",
        background: "#f5f5f5"
    },

    td: {
        border: "1px solid #000",
        padding: "6px 6px",
        fontSize: "12px",
        textAlign: "center",
        height: "26px"
    },

    tdLeft: {
        border: "1px solid #000",
        padding: "6px 6px",
        fontSize: "12px",
        textAlign: "left",
        height: "26px"
    },

    colSl: {
        width: "6%"
    },

    footerTd: {
        border: "1px solid #000",
        borderTop: "none",
        padding: "10px 8px",
        fontSize: "13px"
    },

    footerTdFirstRow: {
        border: "1px solid #000",
        padding: "10px 8px",
        fontSize: "13px"
    },

    footerLabel: {
        fontWeight: "bold",
        width: "22%"
    },

    signatureRow: {
        border: "1px solid #000",
        borderTop: "none",
        padding: "28px 8px 10px 8px",
        fontSize: "13px",
        textAlign: "center",
        fontWeight: "bold"
    }

};

export default function GoodsReturnForm({ returnNo: returnNoProp } = {}) {

    const params = useParams();
    const returnNo = returnNoProp || params.returnNo;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [unit1Location, setUnit1Location] = useState(null); // from Configuration > Location Master (Unit 1 / Regd. Office)

    useEffect(() => {

        const fetchData = async () => {

            try {

                setLoading(true);
                setError("");

                const res = await axios.get(
                    `${API_BASE}/slip/${returnNo}`
                );

                setRows(res.data?.data || []);

            } catch (err) {

                console.log(err);
                setError("Failed to load goods return data");

            } finally {

                setLoading(false);

            }

        };

        if (returnNo) {
            fetchData();
        }

    }, [returnNo]);

    useEffect(() => {
        const loadUnit1Location = async () => {
            try {
                const res = await axios.get(`${MASTER_API}/location-master`);
                const locations = Array.isArray(res.data) ? res.data : [];
                const unit1 = locations.find((loc) => (loc.location_label || "").toLowerCase().includes("1")) || null;
                setUnit1Location(unit1);
            } catch (err) {
                console.error("LOCATION MASTER FETCH ERROR", err);
                setUnit1Location(null);
            }
        };
        loadUnit1Location();
    }, []);

    // Unit 1 (Regd. Office) address, shown under the company name. Falls
    // back to the static text only if Location Master hasn't been set up yet.
    const unit1Name = unit1Location?.company_name || "SBEE CABLES (INDIA) LIMITED";
    const unit1AddressText = unit1Location
        ? `${unit1Location.address_line1}${unit1Location.address_line2 ? ", " + unit1Location.address_line2 : ""}, ${unit1Location.city}${unit1Location.pin_code ? " - " + unit1Location.pin_code : ""}, ${unit1Location.state}`
        : "No.282 & 283, KIADB Industrial Area, Phase 2, Harohalli, Kanakapura Taluk, Ramanagar - 562112, Karnataka, INDIA";

    const first = rows[0] || {};

    const blankRowsToPad = Math.max(0, 6 - rows.length);

    const isEmbedded = !!returnNoProp;

    // Format date helper
    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    };

    // Format qty helper — fixed 2 decimal places, blank/non-numeric untouched
    const formatQty = (value) => {
        if (value === null || value === undefined || value === "") return "";
        const num = Number(value);
        return Number.isNaN(num) ? value : num.toFixed(2);
    };

    const inner = (
        <>
            {!isEmbedded && (
                <div style={styles.toolbar} className="gr-no-print">
                    <button style={styles.printBtn} onClick={() => window.print()}>
                        Print
                    </button>
                </div>
            )}

            {loading && (
                <div style={styles.status}>Loading...</div>
            )}

            {error && (
                <div style={{ ...styles.status, ...styles.error }}>
                    {error}
                </div>
            )}

            {!loading && !error && rows.length === 0 && (
                <div style={styles.status}>
                    No goods return found for {returnNo}
                </div>
            )}

            {!loading && !error && rows.length > 0 && (
                <div style={styles.slip} id="gr-print-area">

                    {/* ── Header ── */}
                    <div style={styles.header}>
                        <img
                            src={sbeeLogo}
                            alt="SBEE Cables logo"
                            style={styles.logo}
                        />
                        <div style={styles.titleBlock}>
                            <div style={styles.title}>
                                {unit1Name}
                            </div>
                            <div style={styles.address}>
                                {unit1AddressText}
                            </div>
                            <div style={styles.subtitle}>
                                MATERIAL RETURN
                            </div>
                        </div>
                    </div>

                    {/* ── Meta info rows ── */}
                    <table style={styles.table}>
                        <colgroup>
                            <col style={{ width: "18%" }} />
                            <col style={{ width: "32%" }} />
                            <col style={{ width: "18%" }} />
                            <col style={{ width: "32%" }} />
                        </colgroup>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.metaTdFirstRow, ...styles.metaLabel }}>
                                    Slip No :
                                </td>
                                <td style={styles.metaTdFirstRow}>
                                    {first.return_id || ""}
                                </td>
                                <td style={{ ...styles.metaTdFirstRow, ...styles.metaLabel }}>
                                    From Location :
                                </td>
                                <td style={styles.metaTdFirstRow}>
                                    {first.from_storage_code
                                        ? `${first.from_storage_code} - ${first.from_storage_location || ""}`
                                        : (first.from_storage_location || "")}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }} colSpan={2}>
                                    &nbsp;
                                </td>
                                <td style={{ ...styles.metaTd, ...styles.metaLabel }}>
                                    To Location :
                                </td>
                                <td style={styles.metaTd}>
                                    {first.to_storage_code
                                        ? `${first.to_storage_code} - ${first.to_storage_location || ""}`
                                        : (first.to_storage_location || "")}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* ── Instruction banner ── */}
                    <div style={styles.instruction}>
                        Kindly issue The Following Items
                    </div>

                    {/* ── Line items table ── */}
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, ...styles.colSl }}>
                                    Sl No.
                                </th>
                                <th style={styles.th}>Product Code</th>
                                <th style={styles.th}>Description</th>
                                <th style={styles.th}>Batch No</th>
                                <th style={styles.th}>Return Qty</th>
                                <th style={styles.th}>UOM</th>
                                <th style={styles.th}>Confirmed Qty</th>
                                <th style={styles.th}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, idx) => (
                                <tr key={idx}>
                                    <td style={styles.td}>{idx + 1}</td>
                                    <td style={styles.td}>{r.product_code || ""}</td>
                                    <td style={styles.tdLeft}>{r.description || ""}</td>
                                    <td style={styles.td}>{r.batch_number || ""}</td>
                                    <td style={styles.td}>{formatQty(r.qty)}</td>
                                    <td style={styles.td}>{r.uom || ""}</td>
                                    <td style={styles.td}></td>
                                    <td style={styles.td}>{r.status || ""}</td>
                                </tr>
                            ))}
                            {Array.from({ length: blankRowsToPad }).map((_, i) => (
                                <tr key={`blank-${i}`}>
                                    <td style={styles.td}>&nbsp;</td>
                                    <td style={styles.td}></td>
                                    <td style={styles.tdLeft}></td>
                                    <td style={styles.td}></td>
                                    <td style={styles.td}></td>
                                    <td style={styles.td}></td>
                                    <td style={styles.td}></td>
                                    <td style={styles.td}></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* ── Footer / Signature ── */}
                    <table style={styles.table}>
                        <tbody>
                            <tr>
                                <td
                                    colSpan={4}
                                    style={{
                                        ...styles.footerTdFirstRow,
                                        textAlign: "center",
                                        fontWeight: "bold",
                                        fontSize: "13px",
                                        borderRight: "1px solid #000"
                                    }}
                                >
                                    Returned By
                                    <br />
                                    <span style={{ fontWeight: "normal" }}>
                                        Name:&nbsp;{first.returned_by || ""}
                                    </span>
                                    <br />
                                    <span style={{ fontWeight: "normal" }}>
                                        Date:&nbsp;{formatDate(first.return_date)}
                                    </span>
                                </td>
                                <td
                                    colSpan={4}
                                    style={{
                                        ...styles.footerTdFirstRow,
                                        textAlign: "center",
                                        fontWeight: "bold",
                                        fontSize: "13px"
                                    }}
                                >
                                    Confirmed By
                                    <br />
                                    <span style={{ fontWeight: "normal" }}>
                                        Name:&nbsp;{first.confirmed_by || ""}
                                    </span>
                                    <br />
                                    <span style={{ fontWeight: "normal" }}>
                                        Date:&nbsp;{first.confirmed_date ? formatDate(first.confirmed_date) : ""}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                </div>
            )}

            <style>
                {`
                    @media print {
                        .gr-no-print {
                            display: none !important;
                        }
                        body {
                            background: #fff !important;
                        }
                        @page {
                            size: A4;
                            margin: 14mm;
                        }
                    }
                `}
            </style>
        </>
    );

    if (isEmbedded) {
        return (
            <div style={{ padding: "16px", overflowY: "auto", height: "100%" }}>
                {inner}
            </div>
        );
    }

    return (
        <div style={styles.wrapper}>
            {inner}
        </div>
    );

}